import cx from "classnames";
import Image from "next/image";
import { match, P } from "ts-pattern";

import { useSettings } from "shared/SettingsProvider/useSettings";
import { useProductPageContext } from "shared/product/ProductPageContext/useProductPageContext";
import { PriceDropBadge } from "ui/PriceDropBadge";
import { formatToCurrency } from "utils/price.utils";
import {
  getSeniorityThreshold,
  usePriceAdvantage,
} from "utils/usePriceAdvantage";
import { useI18n } from "utils/with-i18n.utils";

import { Variant } from "../../../product-section.helper";

import DeviceSync from "./device-sync.svg";

export const CommitmentDetails = ({
  contractTerm,
  currentVariant,
  onClick,
}: {
  contractTerm: Variant;
  currentVariant: Variant | undefined;
  onClick: (sku: Variant) => void;
}) => {
  const i18n = useI18n();
  const { isBlackFriday } = useSettings();

  const subscriptionPriceEuros = Math.floor(
    contractTerm.subscriptionPrice / 100,
  );
  const subscriptionPriceCents = String(
    contractTerm.subscriptionPrice % 100,
  ).padStart(2, "0");

  const seniorityThreshold = getSeniorityThreshold({
    commitmentPeriod: contractTerm.commitmentPeriod,
  });

  const { subscriptionDropSkus } = useProductPageContext();

  const {
    smartphoneFamilyHasSubscriptionPriceDrop,
    smartphoneFamilyHasUpfrontPriceDrop,
    hasSeniorityDiscount,
  } = usePriceAdvantage({
    originalUpfrontPrice: contractTerm.originalUpfrontPrice,
    upfrontPrice: contractTerm.upfrontPrice,
    originalSubscriptionPrice: contractTerm.originalSubscriptionPrice,
    subscriptionPrice: contractTerm.subscriptionPrice,
  });
  const isContractTermSkuInDropList = subscriptionDropSkus?.includes(
    contractTerm.sku as string,
  );

  const isBlackWeekProduct =
    contractTerm?.tagSlugs?.includes("black-week") ?? false;
  const isBlackFridayTheme = isBlackFriday && isBlackWeekProduct;

  // check if the sku of the contractTerm is part of the subscriptionDropSkus
  // i.e., contractTerm's subscription has formerSubscriptionPriceInCentsIncludingTax
  const contractTermHasSubscriptionPromotion =
    smartphoneFamilyHasSubscriptionPriceDrop && isContractTermSkuInDropList;

  const formattedOriginalPriceSubscription = formatToCurrency(
    "fr-FR",
    "EUR",
    contractTerm.originalSubscriptionPrice ?? 0,
    undefined,
    false,
    false,
  );

  const subscriptionPriceLabelOptions = contractTermHasSubscriptionPromotion
    ? {
        originalPrice: formattedOriginalPriceSubscription,
        subscriptionPriceEuros,
        subscriptionPriceCents,
        originalPriceClassName:
          "text-base leading-none text-[#00000080] self-center",
        subscriptionPriceEurosClassName:
          "text-3xl font-bold leading-none items-center text-[#7D89FF]",
        subscriptionPriceCentsClassName:
          "text-sm leading-none font-bold text-[#7D89FF]",
      }
    : {
        subscriptionPriceEuros,
        subscriptionPriceCents,
        subscriptionPriceEurosClassName:
          "text-3xl font-bold leading-none items-center",
        subscriptionPriceCentsClassName: "text-sm leading-none font-bold",
      };

  const subscriptionPriceLabel = contractTermHasSubscriptionPromotion
    ? i18n.t(
        "product.commitmentDetails.subscriptionPriceDiscount",
        subscriptionPriceLabelOptions,
        { html: true },
      )
    : i18n.t(
        "product.commitmentDetails.subscriptionPrice",
        subscriptionPriceLabelOptions,
        { html: true },
      );

  // check if the sku of the contractTerm is part of the subscriptionDropSkus
  // i.e., contractTerm's upfront has formerUpfrontPriceInCentsIncludingTax
  const contractTermHasUpfrontPromotion =
    smartphoneFamilyHasUpfrontPriceDrop && isContractTermSkuInDropList;

  const formattedOriginalPriceUpfront = formatToCurrency(
    "fr-FR",
    "EUR",
    hasSeniorityDiscount
      ? contractTerm.upfrontPrice
      : contractTerm.originalUpfrontPrice ?? 0,
    undefined,
    true,
    false,
  );
  const formattedPriceUpfront = hasSeniorityDiscount
    ? "0 €"
    : formatToCurrency(
        "fr-FR",
        "EUR",
        contractTerm.upfrontPrice ?? 0,
        undefined,
        true,
        false,
      );

  const upfrontPriceLabelOptions =
    contractTermHasUpfrontPromotion || hasSeniorityDiscount
      ? {
          originalPrice: formattedOriginalPriceUpfront,
          upfrontPrice: formattedPriceUpfront,
          originalPriceClassName: "text-base text-[#00000080]",
          upfrontPriceClassName: "text-base font-bold text-[#7D89FF]",
        }
      : {
          upfrontPrice: formattedPriceUpfront,
          upfrontPriceClassName: "font-bold",
        };

  const upfrontPriceLabel =
    contractTermHasUpfrontPromotion || hasSeniorityDiscount
      ? i18n.t(
          "product.commitmentDetails.upfrontPriceDiscount",
          upfrontPriceLabelOptions,
          { html: true },
        )
      : i18n.t(
          "product.commitmentDetails.upfrontPrice",
          upfrontPriceLabelOptions,
          { html: true },
        );

  const discountLabelOptions = match({
    hasSeniorityDiscount,
    contractTermHasUpfrontPromotion,
    contractTermHasSubscriptionPromotion,
  })
    .with({ hasSeniorityDiscount: true }, () => ({}))
    .with(
      {
        hasSeniorityDiscount: P.union(false, P.nullish),
        contractTermHasUpfrontPromotion: true,
      },
      () => ({
        upfrontPriceDifference:
          ((contractTerm.originalUpfrontPrice ?? 0) -
            contractTerm.upfrontPrice) /
          100,
      }),
    )
    .with(
      {
        hasSeniorityDiscount: P.union(false, P.nullish),
        contractTermHasUpfrontPromotion: P.union(false, P.nullish),
        contractTermHasSubscriptionPromotion: true,
      },
      () => ({}),
    )
    .otherwise(() => null);

  const discountLabelKey = match({
    hasSeniorityDiscount,
    contractTermHasUpfrontPromotion,
    contractTermHasSubscriptionPromotion,
  })
    .with(
      { hasSeniorityDiscount: true },
      () => "product.commitmentDetails.freeUpfront",
    )
    .with(
      {
        hasSeniorityDiscount: P.union(false, P.nullish),
        contractTermHasUpfrontPromotion: true,
      },
      () => "product.commitmentDetails.upfrontPriceDifference",
    )
    .with(
      {
        hasSeniorityDiscount: P.union(false, P.nullish),
        contractTermHasUpfrontPromotion: P.union(false, P.nullish),
        contractTermHasSubscriptionPromotion: true,
      },
      () => "product.commitmentDetails.freeSubscription",
    )
    .otherwise(() => null);

  const discountLabel =
    discountLabelKey &&
    discountLabelOptions &&
    i18n.t(discountLabelKey, discountLabelOptions, {
      html: true,
    });

  return (
    <section
      onClick={() => onClick(contractTerm)}
      role="button"
      tabIndex={0}
      className="flex flex-col w-full max-w-md"
    >
      <div
        className={cx(
          "relative border flex flex-col gap-3  justify-between py-4 overflow-hidden",
          contractTerm.isInStock ? "bg-white cursor-pointer" : "cursor-default",
          currentVariant?.sku === contractTerm.sku
            ? "bg-white border-black border-2 border-insite"
            : "border-black/35",
          discountLabel ? "rounded-t-lg border-b-0" : "rounded-lg",
        )}
      >
        <div className={cx(!contractTerm.isInStock && "opacity-50")}>
          {!contractTerm.isInStock && (
            <div className="absolute -top-0 z-20 w-full h-full bg-brown/15" />
          )}

          {isBlackFridayTheme && (
            <div className="flex justify-center items-center">
              <PriceDropBadge className="py-1 px-2 bg-christmas">
                <Image
                  src="/images/christmas/gift.svg"
                  width={16}
                  height={16}
                />
                <p className="font-bold text-transparent uppercase bg-clip-text bg-white">
                  {i18n.t(
                    "catalog.badge.blackFridayPriceDrop",
                    {},
                    { html: true },
                  )}
                </p>
              </PriceDropBadge>
            </div>
          )}

          <div className="flex justify-between px-7 w-full max-w-full">
            <div className="flex flex-col gap-y-1 justify-center items-start leading-none">
              {i18n.t(
                "product.commitmentDetails.commitment",
                { commitmentPeriod: contractTerm.commitmentPeriod },
                { html: true },
              )}

              <span className="font-bold lg:text-lg lg:leading-5">
                {!!contractTerm.commitmentPeriod
                  ? i18n.t("product.commitmentDetails.period", {
                      commitmentPeriod: contractTerm.commitmentPeriod,
                    })
                  : i18n.t("product.commitmentDetails.withoutCommitment")}
              </span>
            </div>

            <div className="flex flex-col justify-center items-end text-sm text-black">
              <div className="flex gap-x-1 items-center">
                {subscriptionPriceLabel}
              </div>

              <div className="flex gap-x-1 items-center">
                {upfrontPriceLabel}
              </div>
            </div>
          </div>

          {!!seniorityThreshold && (
            <div className="flex gap-1.5 justify-center items-center py-2 mx-3 bg-[#7D89FF1A] rounded-lg">
              <DeviceSync />
              <span className="text-xs 2xs:text-sm">
                {i18n.t(
                  "product.commitmentDetails.seniorityThreshold",
                  { seniorityThreshold },
                  { html: true },
                )}
              </span>
            </div>
          )}
        </div>
      </div>

      {discountLabel && (
        <div
          className={cx(
            "flex justify-center items-center py-2 font-black leading-none uppercase bg-[linear-gradient(266.68deg,_#523CB5_1.46%,_#E562FF_91.41%)] rounded-b-lg text-outline-black",
            currentVariant?.sku === contractTerm.sku
              ? "border-2 border-t-0 border-black"
              : "border border-t-0 border-[#00000059]",
          )}
        >
          <div className={cx(!contractTerm.isInStock && "opacity-50")}>
            {!contractTerm.isInStock && (
              <div className="absolute -top-0 z-20 bg-brown/15" />
            )}
            {discountLabel}
          </div>
        </div>
      )}
    </section>
  );
};
